import {AdjustmentsHorizontalIcon} from "@heroicons/react/20/solid";
import {useEffect, useState} from "react";
import {getOtsActionButtonClassName} from "../components/OtsButton";
import {OtsPopover} from "../components/OtsPopover";
import {OtsSwitch} from "../components/OtsSwitch";
import {OtsTextInput} from "../components/OtsTextInput";
import {
  type ContainerStatusDisplayOptions,
  STALE_POLL_HOURS_MAX,
  STALE_POLL_HOURS_MIN,
} from "./container-status-display";

export type ContainerStatusDisplayMenuProps = {
  display: ContainerStatusDisplayOptions;
  onDisplayChange: (display: ContainerStatusDisplayOptions) => void;
};

type Skip = "staleSkipDoNotDispatch" | "staleSkipDoNotUse" | "staleSkipPipeline" | "staleSkipScadaDisabled" | "staleIncludeNeverPolled";

const exceptions: [Skip, string, string][] = [
  ["staleSkipDoNotDispatch", "Skip Do Not Dispatch", "Containers held from dispatch"],
  ["staleSkipDoNotUse", "Skip Do Not Use", "Containers out of service"],
  ["staleSkipPipeline", "Skip pipeline sites", "Sites that move this cargo by pipeline"],
  ["staleSkipScadaDisabled", "Skip SCADA disabled", "Containers with SCADA turned off"],
  ["staleIncludeNeverPolled", "Include never polled", "Containers with no poll at all"],
];

const heading = "text-[10px] font-bold uppercase tracking-wide text-[var(--ots-text-muted)]";

/** The hours field keeps what is typed and only reports whole hours in range. */
const StalePollHoursInput = ({value, disabled, onChange}: {value: number; disabled: boolean; onChange: (hours: number) => void}) => {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  const hours = Number(draft);
  const valid = Number.isInteger(hours) && hours >= STALE_POLL_HOURS_MIN && hours <= STALE_POLL_HOURS_MAX;
  return (
    <OtsTextInput
      size="compact"
      type="number"
      inputMode="numeric"
      min={STALE_POLL_HOURS_MIN}
      max={STALE_POLL_HOURS_MAX}
      step={1}
      label="Hours without a poll"
      disabled={disabled}
      value={draft}
      error={valid ? undefined : `${STALE_POLL_HOURS_MIN}–${STALE_POLL_HOURS_MAX} hours`}
      containerClassName="w-40"
      onChange={(event) => {
        setDraft(event.target.value);
        const next = Number(event.target.value);
        if (Number.isInteger(next) && next >= STALE_POLL_HOURS_MIN && next <= STALE_POLL_HOURS_MAX) onChange(next);
      }}
      onBlur={() => setDraft(String(value))}/>
  );
};

/** How the container grid marks rows: currently the stale poll highlight, its age, and which containers it skips. */
export const ContainerStatusDisplayMenu = ({display, onDisplayChange}: ContainerStatusDisplayMenuProps) => (
  <OtsPopover
    density="compact"
    placement="bottom end"
    ariaLabel="Display options"
    tooltip="Display options"
    trigger={<AdjustmentsHorizontalIcon aria-hidden="true" className="size-[18px]"/>}
    buttonClassName={getOtsActionButtonClassName("text", "compact", "!size-8 !p-0")}
    panelClassName="flex w-80 !max-w-[calc(100vw-2rem)] flex-col gap-3 !p-3">
    <fieldset className="flex flex-col gap-2">
      <legend className={[heading, "mb-1.5"].join(" ")}>Stale polls</legend>
      <OtsSwitch
        size="compact"
        label="Highlight stale polls"
        description="Mark rows that have gone too long without a poll"
        checked={display.markStalePolls}
        onCheckedChange={(markStalePolls) => onDisplayChange({...display, markStalePolls})}/>
      <StalePollHoursInput
        value={display.stalePollHours}
        disabled={!display.markStalePolls}
        onChange={(stalePollHours) => onDisplayChange({...display, stalePollHours})}/>
    </fieldset>
    <fieldset className="flex flex-col gap-2" disabled={!display.markStalePolls}>
      <legend className={[heading, "mb-1.5"].join(" ")}>Applies to</legend>
      {exceptions.map(([key, label, description]) => (
        <OtsSwitch
          key={key}
          size="compact"
          label={label}
          description={description}
          disabled={!display.markStalePolls}
          checked={display[key]}
          onCheckedChange={(checked) => onDisplayChange({...display, [key]: checked})}/>
      ))}
    </fieldset>
  </OtsPopover>
);
